import type { ReactNode } from 'react'
import type { ProductsHeader } from '@/types/treeViewTypes'
import { VpIcon, VpLink, VpSkeleton } from '@vtmn-play/react'
import { useTranslation } from 'react-i18next'
import { FamiliesModal } from '@/components/Families/FamiliesModal'
import { useProductsHeaderFamilies } from '@/hooks/products/useProductsHeaderFamilies'
import { ANALYTICS_EVENTS, trackAnalyticsEvent } from '@/services/analytics'

export function ProductsPageHeaderTitle({
  contextId,
  data,
  isLoading,
  label,
  prefix,
}: Readonly<{
  contextId: string
  data?: ProductsHeader
  isLoading: boolean
  label: ReactNode
  prefix: ReactNode
}>) {
  const { t } = useTranslation()

  const { isFamiliesModalOpen, setIsFamiliesModalOpen, multiFamilies, ids, chipFamilies, onValidate } = useProductsHeaderFamilies(data?.subdepartments)
  const isFamilyIdPrefix = contextId === 'title_single-family' && typeof prefix === 'number'

  const handleOpenFamiliesModal = () => {
    trackAnalyticsEvent(ANALYTICS_EVENTS.PRODUCTS_SELECTED_FAMILIES_MODAL_OPENED)
    setIsFamiliesModalOpen(true)
  }

  const handleValidateFamilies = (families: typeof chipFamilies) => {
    const familiesRemovedCount = chipFamilies.length - families.length

    if (familiesRemovedCount > 0) {
      trackAnalyticsEvent(ANALYTICS_EVENTS.PRODUCTS_SELECTED_FAMILIES_REMOVAL_CONFIRMED, {
        families_removed_count: familiesRemovedCount,
      })
    }

    return onValidate(families)
  }

  if (isLoading && !multiFamilies) {
    return (
      <div className="products-header__title">
        <div className="products-header__title__prefix">
          <VpSkeleton shape="squared" style={{ width: '144px', height: '22px' }} />
        </div>
        <div className="products-header__title__label">
          <VpSkeleton shape="squared" style={{ width: '240px', height: '48px' }} />
        </div>
      </div>
    )
  }

  return (
    <div className="products-header__title" data-testid={contextId}>
      {!multiFamilies && (
        <div className="products-header__title__prefix">
          {isFamilyIdPrefix
            ? (
                <>
                  <span className="sr-only">
                    {t('products-page.selected-family-id', { id: prefix })}
                  </span>
                  <span aria-hidden="true">{prefix}</span>
                </>
              )
            : prefix}
        </div>
      )}
      <h1 className="products-header__title__label">
        {label}
        {ids.length > 1 && <span>{` (${ids.length})`}</span>}
      </h1>
      {multiFamilies && (
        <>
          <VpLink
            onClick={handleOpenFamiliesModal}
            variant="tertiary"
            size="small"
            data-testid="see-families-button"
          >
            {t('products-page.view-selected-families')}
            <VpIcon name="chevron-right" />
          </VpLink>
          <FamiliesModal
            families={chipFamilies}
            openModal={isFamiliesModalOpen}
            validate={handleValidateFamilies}
            setOpenModal={setIsFamiliesModalOpen}
          />
        </>
      )}
    </div>
  )
}
